/**
 * S1b · CONDITIONS SHADE + the DRIFT strip.
 *
 * Conditions are CAPACITY, not metrics: `can it still fire?`. `UNKNOWN` is
 * never rendered as GO and never rolls up as GO — a stale snapshot greys
 * every row and stamps the card `SNAPSHOT n OLD — this page cannot tell you
 * anything`. DOWN is drawn with FORM, not with red: red on this surface
 * competes with the sign of a number and with nothing else on the deck.
 *
 * TENANCY DEPARTURE (ARCHITECTURE §4 · D-T3): a customer sees the condition
 * STATE and the `blocks:` clause — capacity is their business — but never
 * node identities, peer hostnames, poll rates or first-sighting share. Fleet
 * shape is the operator's infrastructure. `projected: true` says so on the
 * card rather than leaving the omission to be discovered.
 *
 * The DRIFT strip answers M9 — "which hop moved" — in six rows and replaces
 * the old `/latency` page, an endpoint that was fully implemented and had
 * zero consumers (D22). It compares the reader's bracket against the same
 * length immediately preceding it, and BOTH COLUMN LABELS COME OFF THE
 * PAYLOAD: a hardcoded "24h / 7d" header over a server that computed
 * something else is a lie the client would have no way to notice. SPAN SCOPE
 * is its expansion over the SAME Span objects — one renderer, expanded, not a
 * second component — and it is a native <details>, so the disclosure marker is
 * drawn by CSS rather than by a glyph no brand face carries.
 */

import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { api, type Certainty, type Clock } from "../lib/api";
import { BracketBar } from "../lib/BracketBar";
import { refetchMsFor, scopeParam, useRange } from "../lib/bracket";
import { CLOCK_MARK, fmtMs, prefix } from "../lib/span";
import { age, stamp } from "../lib/time";

export function Conditions() {
  const range = useRange();
  const bracketArgs = { since: range.since, until: range.until, scope_label: scopeParam(range) };
  const refetchInterval = refetchMsFor(range);

  const q = useQuery({ queryKey: ["conditions"], queryFn: api.conditions, staleTime: 0, gcTime: 0 });
  // DRIFT compares the bracket against the same length immediately before it.
  // Both column labels come off the payload, so the table can never claim a
  // period the server did not compute.
  const drift = useQuery({
    queryKey: ["drift", range.since, range.until],
    queryFn: () => api.drift(bracketArgs),
    refetchInterval,
  });
  const [scope, setScope] = useState(false);
  const spans = useQuery({
    queryKey: ["spans", range.since, range.until],
    queryFn: () => api.spans(bracketArgs),
    enabled: scope,
    refetchInterval,
  });

  const driftRows = drift.data?.rows ?? [];
  const curLabel = drift.data?.cur_label ?? range.label;
  const prevLabel = drift.data?.prev_label ?? "prior";
  const conds = q.data?.conditions ?? [];
  const stalest = conds.reduce<number | null>(
    (acc, c) => (c.stale_age_s === null || c.stale_age_s === undefined ? acc : Math.max(acc ?? 0, c.stale_age_s)),
    null,
  );

  return (
    <section className="surface">
      <header className="surface__head">
        <h1 className="surface__title">Conditions</h1>
        {q.data && <div className="surface__meta mono">read {stamp(conds[0]?.read_at)}</div>}
      </header>

      <BracketBar />

      <div className="surface__body">
        {stalest !== null && stalest > 600 && (
          <div className="callout callout--stale">SNAPSHOT {age(stalest)} OLD — this page cannot tell you anything.</div>
        )}

        <div className="conds">
          {conds.length === 0 && <div className="stub">no condition samples ingested yet</div>}
          {conds.map((c) => (
            <div key={c.name} className={`condrow condrow--${c.state.toLowerCase()}`}>
              <span className="condrow__name mono">{c.name}</span>
              <span className="condrow__state">{c.state}</span>
              <span className="note">
                {c.degraded_telemetry
                  ? // Degraded ≠ zero: the {degraded:true} snapshot shape has
                    // most keys absent and renders as unavailable, never as 0.
                    "telemetry unavailable — not zeros"
                  : (c.blocks ?? "")}
              </span>
              {c.stale_age_s !== null && c.stale_age_s !== undefined && c.stale_age_s > 0 && (
                <span
                  className="chip"
                  title="cached-behind-fresh-read: a stale_age in an otherwise-current dump is the exact shape that lets a dead bridge look alive"
                >
                  stale {age(c.stale_age_s)}
                </span>
              )}
            </div>
          ))}
        </div>

        {q.data?.projected && (
          <span
            className="chip"
            title="node identities, peer hostnames and poll rates are operator infrastructure and live on the ADMIN surface. What is on this card is capacity: whether the machine can still fire for you."
          >
            projected
          </span>
        )}

        <section className="band">
          <div className="band__title" title={drift.data?.note}>
            DRIFT
          </div>
          <div className="table">
            <table className="drift mono">
              <thead>
                <tr>
                  <th>hop</th>
                  <th className="num">{curLabel} p50</th>
                  <th className="num">{prevLabel} p50</th>
                  <th className="num">Δ</th>
                  <th className="num">n</th>
                </tr>
              </thead>
              <tbody>
                {driftRows.map((r) => {
                  // A BOUNDED row never gets a percentile-shaped delta, so it
                  // never gets a sign colour either — "bounded" is a word.
                  const signed = r.certainty !== "BOUNDED" && r.delta_pct !== null && r.delta_pct !== undefined;
                  const deltaCls = signed ? (r.delta_pct! > 0 ? "num down" : "num up") : "num dim";
                  return (
                    <tr key={r.hop} data-certainty={r.certainty ?? "UNMEASURABLE"}>
                      <td>
                        <span className="span__clock">{r.clock ? CLOCK_MARK[r.clock as Clock] : "∅"}</span> {r.hop}
                      </td>
                      <td className="num">
                        {prefix(r.certainty as Certainty)}
                        {fmtMs(r.p50_cur)}
                      </td>
                      <td className="num">
                        {prefix(r.certainty as Certainty)}
                        {fmtMs(r.p50_prev)}
                      </td>
                      <td className={deltaCls}>
                        {r.certainty === "BOUNDED" ? "bounded" : r.delta_pct === null ? "—" : `${r.delta_pct}%`}
                      </td>
                      <td className="num dim" title={`${curLabel} / ${prevLabel}`}>
                        {r.n_cur}/{r.n_prev}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        <details className="disclose" open={scope} onToggle={(e) => setScope(e.currentTarget.open)}>
          <summary>SPAN SCOPE</summary>

          <section className="band">
            <div className="band__title">SPAN SCOPE · {spans.data?.window?.label ?? range.label}</div>
            <div className="table">
              <table className="drift mono">
                <thead>
                  <tr>
                    <th>hop</th>
                    <th className="num">n</th>
                    <th className="num">p50</th>
                    <th className="num">p90</th>
                    <th className="num">p99</th>
                    <th title="bounded and unmeasurable rows sit here permanently with ≤ and ∅ — a standing argument for P1 and P6">
                      certainty
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(spans.data?.hops ?? []).map((h: any) => (
                    <tr key={h.hop} data-certainty={h.certainty ?? "UNMEASURABLE"}>
                      <td>
                        <span className="span__clock">{h.clock ? CLOCK_MARK[h.clock as Clock] : "∅"}</span> {h.hop}
                      </td>
                      <td className="num dim">{h.n}</td>
                      <td className="num">
                        {prefix(h.certainty)}
                        {fmtMs(h.p50)}
                      </td>
                      <td className="num">
                        {prefix(h.certainty)}
                        {fmtMs(h.p90)}
                      </td>
                      <td className="num">
                        {prefix(h.certainty)}
                        {fmtMs(h.p99)}
                      </td>
                      <td className="dim">{h.certainty}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="band">
            <div className="band__title">RACE LEDGER</div>
            <div className="figs">
              {(spans.data?.race_ledger?.gate_losses ?? []).map((g: any) => (
                <div key={g.rung} className="fig">
                  <span className="fig__k mono">{g.rung}</span>
                  <span className="fig__v mono num">×{g.n}</span>
                </div>
              ))}
            </div>
            <div className="figs">
              <div className="fig">
                <span className="fig__k">fired</span>
                <span className="fig__v mono num">{spans.data?.race_ledger?.counts?.fired ?? 0}</span>
              </div>
              <div className="fig">
                <span className="fig__k">covered</span>
                <span className="fig__v mono num">{spans.data?.race_ledger?.counts?.covered ?? 0}</span>
              </div>
              <div className="fig">
                <span className="fig__k">wrong-direction</span>
                <span className="fig__v mono num">{spans.data?.race_ledger?.counts?.wrong_direction ?? 0}</span>
              </div>
              <div className="fig">
                <span className="fig__k">unverified</span>
                <span className="fig__v mono num">{spans.data?.race_ledger?.counts?.unverified ?? 0}</span>
              </div>
              <div className="fig" title="cases whose fate depends on the gate constant">
                <span className="fig__k">boundary zone</span>
                <span className="fig__v mono num">{(spans.data?.race_ledger?.boundary_zone ?? []).length}</span>
              </div>
            </div>
          </section>
        </details>
      </div>
    </section>
  );
}
